import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowDown, ArrowUp, MoveDown, MoveUp } from 'lucide-react';
import React, { useEffect, useReducer } from 'react';
import InspectorSection from '../shared/inspector-section';
import type { ObjectInspectorProps } from '../types';
import { theme } from 'styles/theme/theme';

const LayerSection: React.FC<ObjectInspectorProps> = ({ objects, editor }) => {
  const { t } = useLingui();
  const [, bump] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    const offMod = editor.on('object:modified', bump);
    const offAdd = editor.on('object:added', bump);
    const offRem = editor.on('object:removed', bump);
    const offLoad = editor.on('canvas:loaded', bump);
    return () => {
      offMod();
      offAdd();
      offRem();
      offLoad();
    };
  }, [editor]);

  const layers = editor.layers;
  if (!layers || objects.length === 0) return null;

  // Single → target the one object. Multi → no target, the façade resolves the
  // active selection and applies to every member.
  const target = objects.length === 1 ? objects[0] : undefined;
  const canUp = layers.canMoveUp(target);
  const canDown = layers.canMoveDown(target);

  return (
    <InspectorSection title={t`Слой`}>
      <Grid>
        <Button type="button" disabled={!canUp} onClick={() => layers.bringForward(target)}>
          <ArrowUp size={14} />
          <span>
            <Trans>На слой выше</Trans>
          </span>
        </Button>
        <Button type="button" disabled={!canUp} onClick={() => layers.bringToFront(target)}>
          <MoveUp size={14} />
          <span>
            <Trans>На верх</Trans>
          </span>
        </Button>
        <Button type="button" disabled={!canDown} onClick={() => layers.sendBackwards(target)}>
          <ArrowDown size={14} />
          <span>
            <Trans>На слой ниже</Trans>
          </span>
        </Button>
        <Button type="button" disabled={!canDown} onClick={() => layers.sendToBack(target)}>
          <MoveDown size={14} />
          <span>
            <Trans>На низ</Trans>
          </span>
        </Button>
      </Grid>
    </InspectorSection>
  );
};

export default LayerSection;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  font-size: 12px;
  color: ${theme.colors.DarkBlue600};
  background: ${theme.colors.SurfaceWhite};
  border: 1px solid ${theme.colors.DarkBlue100};
  border-radius: 4px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${theme.colors.SurfaceCultured10};
  }

  &:disabled {
    color: ${theme.colors.DarkBlue300};
    cursor: not-allowed;
    background: ${theme.colors.BorderGray50};
  }
`;
